import React, { useRef } from "react";
import {
  FaPrint, FaTimes, FaCheckCircle, FaCar, FaFileInvoiceDollar,
  FaShieldAlt, FaIdCard, FaBuilding, FaUserCheck, FaLock, FaDownload
} from "react-icons/fa";

const DealPrintReport = ({
  isOpen,
  onClose,
  deal,
  customerInfo = {},
  uploadedDocs = {},
  salespersonId = "",
  dealershipData = {},
  paymentDetails = {}
}) => {
  const reportRef = useRef(null);

  if (!isOpen) return null;

  const vehicle = deal?.vehicle || {};
  const finance = deal?.finance || {};
  const trade = deal?.trade || {};
  const signature = deal?.signature || {};
  const approval = deal?.approvalDetails || {};

  const customerName = customerInfo.name || `${customerInfo.firstName || ''} ${customerInfo.lastName || ''}`.trim() || deal?.customer?.name || "Valued Customer";
  const customerPhone = customerInfo.phone || deal?.customer?.phone || "(555) 234-5678";
  const customerEmail = customerInfo.email || deal?.customer?.email || "customer@example.com";
  const customerAddress = customerInfo.address || deal?.customer?.address || "123 Main Street";
  const customerCity = customerInfo.city || deal?.customer?.city || "Salisbury";
  const customerState = customerInfo.state || deal?.customer?.state || "MD";
  const customerZip = customerInfo.zip || deal?.customer?.zip || "21801";

  const salePrice = Number(vehicle.salePrice || vehicle.sale_price || vehicle.msrp || 34200);
  const docFee = Number(vehicle.docFee || dealershipData.doc_fee || 800);
  const regFee = Number(vehicle.registration || dealershipData.registration_fee || 772);
  const taxRate = Number(dealershipData.tax_rate || 0.07);
  const salesTax = Math.round(salePrice * taxRate);
  const totalAmountDue = salePrice + docFee + regFee + salesTax;

  const downPayment = Number(finance.downPayment || paymentDetails.downPayment || 0);
  const customerRebate = Number(paymentDetails.customerRebate || 0);
  const tradeEquity = Number(trade.equity || paymentDetails.tradeEquity || 0);
  const selectedProducts = paymentDetails.selectedProducts || deal?.fAndI?.products || [];
  const productTotal = Number(paymentDetails.productTotal || 0);
  const totalFinanced = Math.max(0, totalAmountDue + productTotal - tradeEquity - downPayment - customerRebate);

  const paymentPreference = paymentDetails.paymentPreference || deal?.finance?.paymentType || "FINANCE";
  const apr = Number(finance.apr || paymentDetails.apr || 5.99);
  const termMonths = Number(finance.term || paymentDetails.term || 72);
  const monthlyPayment = Number(paymentDetails.monthlyPayment || (totalFinanced > 0 ? (totalFinanced * (apr / 1200)) / (1 - Math.pow(1 + (apr / 1200), -termMonths)) : 0));

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/85 backdrop-blur-md flex items-center justify-center p-2 sm:p-4 overflow-y-auto print:p-0 print:bg-white print:static print:inset-auto">
      {/* Top Action Controls (Hidden on Print) */}
      <div className="fixed top-4 right-4 z-[110] flex items-center gap-3 print:hidden">
        <button
          type="button"
          onClick={handlePrint}
          className="bg-gradient-to-r from-emerald-400 via-teal-400 to-cyan-400 text-slate-950 font-black px-5 py-2.5 rounded-2xl text-xs uppercase tracking-wider shadow-2xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2 cursor-pointer"
        >
          <FaPrint size={14} /> Print / Save as PDF
        </button>
        <button
          type="button"
          onClick={onClose}
          className="bg-[#121a2f] hover:bg-slate-800 text-slate-300 hover:text-white border border-slate-700 px-4 py-2.5 rounded-2xl text-xs font-bold transition-colors cursor-pointer"
        >
          <FaTimes size={14} />
        </button>
      </div>

      {/* Official Automotive Deal Report Paper Document */}
      <div
        id="deal-print-document"
        ref={reportRef}
        className="bg-white text-slate-900 w-full max-w-4xl rounded-2xl shadow-2xl p-6 sm:p-10 space-y-6 my-auto font-sans print:shadow-none print:m-0 print:p-6 print:max-w-none print:w-full print:rounded-none"
        style={{ colorScheme: "light" }}
      >
        {/* REPORT HEADER */}
        <div className="border-b-2 border-slate-900 pb-4 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="space-y-1">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-slate-900 text-white flex items-center justify-center font-black text-sm">
                <FaCar />
              </div>
              <div>
                <h1 className="text-xl font-black uppercase tracking-tight text-slate-950">
                  {dealershipData.name || "POHANKA AUTOMOTIVE GROUP"}
                </h1>
                <p className="text-[10px] text-slate-500 font-semibold tracking-wider uppercase">
                  Authorized Dealership &middot; Customer Sales Agreement &middot; Buyer's Order Recapitulation
                </p>
              </div>
            </div>
          </div>

          <div className="text-left sm:text-right space-y-0.5 border-t sm:border-t-0 pt-2 sm:pt-0 border-slate-200 w-full sm:w-auto">
            <span className="inline-block bg-slate-900 text-white text-[10px] font-black px-2.5 py-0.5 rounded tracking-widest uppercase">
              DEAL #{deal?.dealNumber || "PHS-00124"}
            </span>
            <p className="text-[10px] text-slate-600 font-bold">
              Date: {new Date().toLocaleDateString("en-US", { year: 'numeric', month: 'short', day: 'numeric' })} &middot; {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </p>
            <p className="text-[10px] text-slate-500">
              Sales Consultant: <strong>{approval.approvedBy || salespersonId || "Sales Consultant"}</strong>
            </p>
          </div>
        </div>

        {/* SECTION 1: CUSTOMER & VEHICLE INFORMATION GRID */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs">
          {/* Buyer Information Box */}
          <div className="border border-slate-300 rounded-xl p-3.5 space-y-2 bg-slate-50/50">
            <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
              1. BUYER INFORMATION
            </span>
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Customer Full Name</span>
                <strong className="text-slate-900 text-[11px]">{customerName}</strong>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Primary Phone</span>
                <span className="text-slate-900 font-medium">{customerPhone}</span>
              </div>
              <div className="col-span-2">
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Billing &amp; Residence Address</span>
                <span className="text-slate-900 font-medium">{customerAddress}, {customerCity}, {customerState} {customerZip}</span>
              </div>
            </div>
          </div>

          {/* Vehicle Information Box */}
          <div className="border border-slate-300 rounded-xl p-3.5 space-y-2 bg-slate-50/50">
            <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
              2. VEHICLE PURCHASED
            </span>
            <div className="grid grid-cols-2 gap-2 text-slate-700">
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Year / Make / Model</span>
                <strong className="text-slate-900 text-[11px]">{vehicle.year || 2026} {vehicle.make || 'Toyota'} {vehicle.model || 'Camry'}</strong>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Trim &amp; Finish</span>
                <span className="text-slate-900 font-medium">{vehicle.trim || 'XSE'} &middot; {vehicle.color || 'Supersonic Red'}</span>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Stock Number</span>
                <span className="font-mono font-bold text-slate-900">{vehicle.stockNumber || vehicle.stock_number || 'TY26881'}</span>
              </div>
              <div>
                <span className="text-[8px] text-slate-400 uppercase font-bold block">Odometer / Condition</span>
                <span className="text-slate-900 font-medium">{vehicle.mileage ? `${vehicle.mileage.toLocaleString()} mi` : 'Brand New (8 mi)'}</span>
              </div>
            </div>
          </div>
        </div>

        {/* SECTION 2: ITEMIZED FINANCIAL CALCULATION & SETTLEMENT */}
        <div className="border border-slate-300 rounded-xl overflow-hidden text-xs">
          <div className="bg-slate-900 text-white px-4 py-2 flex justify-between items-center">
            <span className="text-[10px] font-black uppercase tracking-widest">3. FINANCIAL RECAPITULATION &amp; SETTLEMENT BREAKDOWN</span>
            <span className="text-[9px] font-bold text-slate-300 uppercase">Payment Mode: {paymentPreference}</span>
          </div>

          <table className="w-full text-left border-collapse">
            <tbody className="divide-y divide-slate-200">
              <tr className="bg-white">
                <td className="py-2 px-4 text-slate-700 font-medium">Agreed Vehicle Purchase Price (MSRP / Sale Price)</td>
                <td className="py-2 px-4 text-right font-bold text-slate-900">${salePrice.toLocaleString()}</td>
              </tr>
              <tr className="bg-slate-50/60">
                <td className="py-2 px-4 text-slate-700">Dealer Processing &amp; Documentation Fee</td>
                <td className="py-2 px-4 text-right font-bold text-slate-900">${docFee.toLocaleString()}</td>
              </tr>
              <tr className="bg-white">
                <td className="py-2 px-4 text-slate-700">State Motor Vehicle Title &amp; Registration Fee</td>
                <td className="py-2 px-4 text-right font-bold text-slate-900">${regFee.toLocaleString()}</td>
              </tr>
              <tr className="bg-slate-50/60">
                <td className="py-2 px-4 text-slate-700">State Sales Tax ({(taxRate * 100).toFixed(1)}%)</td>
                <td className="py-2 px-4 text-right font-bold text-slate-900">${salesTax.toLocaleString()}</td>
              </tr>

              {/* Total Purchase Price */}
              <tr className="bg-slate-100 font-bold">
                <td className="py-2 px-4 text-slate-900">Total Purchase Amount Due</td>
                <td className="py-2 px-4 text-right text-slate-950">${totalAmountDue.toLocaleString()}</td>
              </tr>

              {/* Trade-in Section */}
              {trade.hasTrade && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold">
                  <td className="py-2 px-4">Trade-in Equity Reduction ({trade.year || 2021} {trade.model || 'Trade Vehicle'})</td>
                  <td className="py-2 px-4 text-right text-emerald-700">-${Math.abs(tradeEquity).toLocaleString()}</td>
                </tr>
              )}

              {/* Cash Down & Rebates */}
              {downPayment > 0 && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold">
                  <td className="py-2 px-4">Customer Cash Down Payment</td>
                  <td className="py-2 px-4 text-right text-emerald-700">-${downPayment.toLocaleString()}</td>
                </tr>
              )}
              {customerRebate > 0 && (
                <tr className="bg-emerald-50/60 text-emerald-900 font-semibold">
                  <td className="py-2 px-4">Manufacturer / Customer Promotional Rebate</td>
                  <td className="py-2 px-4 text-right text-emerald-700">-${customerRebate.toLocaleString()}</td>
                </tr>
              )}

              {/* Protection Products */}
              {selectedProducts.length > 0 && (
                <tr className="bg-cyan-50/60 text-cyan-950 font-medium">
                  <td className="py-2 px-4">
                    <span>Dealership Protection Plans ({selectedProducts.length} items):</span>
                    <ul className="text-[10px] text-slate-600 list-disc list-inside mt-0.5 space-y-0.5">
                      {selectedProducts.map((p, idx) => (
                        <li key={idx}>
                          {p.name || p.short_name} (+${Number(p.price).toLocaleString()})
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td className="py-2 px-4 text-right font-bold text-cyan-900 align-top">+${productTotal.toLocaleString()}</td>
                </tr>
              )}

              {/* FINAL NET FINANCED OR CASH DUE */}
              <tr className="bg-slate-900 text-white font-black text-sm">
                <td className="py-3 px-4 uppercase tracking-wider">
                  {paymentPreference === "CASH" ? "TOTAL CASH SETTLEMENT AMOUNT DUE" : "TOTAL AMOUNT FINANCED"}
                </td>
                <td className="py-3 px-4 text-right text-base text-cyan-300">
                  ${(paymentPreference === "CASH" ? Math.max(0, totalAmountDue - tradeEquity - customerRebate) : totalFinanced).toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* SECTION 3: PAYMENT STRUCTURE & LENDER TERMS */}
        {paymentPreference === "FINANCE" && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs bg-slate-50 border border-slate-300 p-3.5 rounded-xl text-center">
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Lender Partner</span>
              <strong className="text-slate-900 text-[11px] truncate block">Toyota Financial Services</strong>
            </div>
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Term</span>
              <strong className="text-slate-900 text-[11px]">{termMonths} Months</strong>
            </div>
            <div className="border-r border-slate-200 last:border-r-0">
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Annual Percentage Rate</span>
              <strong className="text-slate-900 text-[11px]">{apr}% APR</strong>
            </div>
            <div>
              <span className="text-[8px] text-slate-500 uppercase font-bold block">Monthly Payment</span>
              <strong className="text-cyan-700 text-sm font-black">${Math.round(monthlyPayment)}/mo</strong>
            </div>
          </div>
        )}

        {/* SECTION 4: ATTACHED DOCUMENTS & COMPLIANCE VERIFICATION */}
        <div className="border border-slate-300 rounded-xl p-3.5 space-y-2 text-xs bg-slate-50/30">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-500 block border-b border-slate-200 pb-1">
            4. DOCUMENTATION &amp; COMPLIANCE VERIFICATION
          </span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[10px]">
            {["Driver's License (Front)", "Driver's License (Back)", "Proof of Income", "Insurance Card"].map((slot) => (
              <div key={slot} className="bg-white p-2 rounded-lg border border-slate-200 flex items-center justify-between">
                <span className="text-slate-700 font-semibold truncate">{slot}</span>
                <span className="text-emerald-600 font-black flex items-center gap-1">
                  <FaCheckCircle size={10} /> Verified
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* SECTION 5: LEGAL SIGNATURES & AUTHORIZATION */}
        <div className="border-2 border-slate-900 rounded-xl p-4 space-y-3 bg-white">
          <span className="text-[9px] font-black uppercase tracking-wider text-slate-900 block border-b border-slate-200 pb-1">
            5. ELECTRONIC SIGNATURE &amp; LEGAL CERTIFICATION
          </span>
          <p className="text-[9px] text-slate-500 leading-tight">
            The Buyer certifies under penalty of perjury that all submitted information and uploaded documentation are true, accurate, and complete. This agreement conforms with the U.S. Electronic Signatures in Global and National Commerce Act (E-SIGN).
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
            {/* Customer Signature Box */}
            <div className="border border-slate-300 rounded-xl p-3 bg-slate-50/50 flex flex-col justify-between h-32">
              <div className="flex justify-between items-start">
                <span className="text-[8px] font-black uppercase text-slate-600">Buyer Digital Signature</span>
                <span className="text-[8px] text-emerald-600 font-bold">✓ Verified E-Sign</span>
              </div>

              {signature.dataUrl ? (
                <div className="h-14 flex items-center justify-center overflow-hidden my-1">
                  <img
                    src={signature.dataUrl}
                    alt="Customer Signature"
                    className="max-h-full max-w-full object-contain"
                  />
                </div>
              ) : (
                <div className="text-center font-serif italic text-sm text-slate-700 my-auto">
                  {customerName}
                </div>
              )}

              <div className="flex justify-between items-center text-[8px] text-slate-500 border-t border-slate-200 pt-1">
                <span>Signer: <strong>{signature.signedBy || customerName}</strong></span>
                <span>Signed: {signature.signedAt ? new Date(signature.signedAt).toLocaleDateString() : new Date().toLocaleDateString()}</span>
              </div>
            </div>

            {/* Dealership Manager Signature Box */}
            <div className="border border-slate-300 rounded-xl p-3 bg-slate-50/50 flex flex-col justify-between h-32">
              <div className="flex justify-between items-start">
                <span className="text-[8px] font-black uppercase text-slate-600">Authorized Dealership Approval</span>
                <span className="text-[8px] text-emerald-600 font-bold">✓ Approved &amp; Funded</span>
              </div>

              <div className="my-auto text-center">
                <span className="font-serif italic font-bold text-slate-800 text-sm block">
                  {approval.approvedBy || salespersonId || "Bobby Bonser (Sales Consultant)"}
                </span>
                <span className="text-[8px] text-slate-500 uppercase tracking-widest">Authorized Dealership Finance Officer</span>
              </div>

              <div className="flex justify-between items-center text-[8px] text-slate-500 border-t border-slate-200 pt-1">
                <span>Approved: {approval.approvedAt ? new Date(approval.approvedAt).toLocaleDateString() : new Date().toLocaleDateString()}</span>
                <span>Status: <strong>CONTRACT APPROVED</strong></span>
              </div>
            </div>
          </div>
        </div>

        {/* PRINT FOOTER */}
        <div className="pt-2 flex justify-between items-center text-[8px] text-slate-400 border-t border-slate-200">
          <span>System Generated by Pohanka Dealership 2.0 AI Deal Studio &middot; 256-Bit Encrypted Record</span>
          <span>Page 1 of 1 &middot; Deal Reference #{deal?.dealNumber || "PHS-00124"}</span>
        </div>
      </div>

      {/* Global CSS for Print Mode to Ensure Clean 1-Page Document Output */}
      <style dangerouslySetInnerHTML={{
        __html: `
        @media print {
          @page {
            size: letter portrait;
            margin: 10mm;
          }
          body * {
            visibility: hidden !important;
          }
          #deal-print-document, #deal-print-document * {
            visibility: visible !important;
          }
          #deal-print-document {
            position: absolute !important;
            left: 0 !important;
            top: 0 !important;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
            background: white !important;
            color: #0f172a !important;
            page-break-after: avoid !important;
            page-break-inside: avoid !important;
          }
        }
      `}} />
    </div>
  );
};

export default DealPrintReport;
